import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftRight, CirclePlus, Printer, Search } from "lucide-react";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import { Page, Card, Alert, Button, EmptyState, InlineLoading, KPI, KPIGrid, ResponsiveGrid, Table, Text, Badge, type Column } from "@marekl/adminer/components";
import { toApiDate } from "@marekl/adminer/datetime";
import { ScanInput } from "../../components/ScanInput";
import { useTodaySummary, printDailyReport } from "../../lib/queries/reports";
import { aggregateLatestRides, type AggregatedRide } from "../../lib/aggregateRides";
import { businessToday } from "../../lib/businessDay";
import { useVoucherLookup, type LookupResponse } from "../../lib/queries/vouchers";
import { useLookupCertificate } from "../../lib/queries/certificates";
import { classifyScan } from "../../lib/scan";
import { ApiError } from "../../lib/api";
import { formatDateTime } from "@marekl/adminer/format";
import { useScanKeyboardCapture } from "../../lib/useScanKeyboardCapture";
import styles from "./DashboardPage.module.css";

function lookupErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    if (body?.message) return body.message;
  }
  return "Nepodařilo se ověřit kód, zkuste to prosím znovu.";
}

export function DashboardPage() {
  const navigate = useNavigate();
  const summary = useTodaySummary();
  const lookup = useVoucherLookup();
  const certLookup = useLookupCertificate();

  const [scanValue, setScanValue] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanInfo, setScanInfo] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  useScanKeyboardCapture(scanRef, (v) => {
    setScanValue(v);
    setScanError(null);
    setScanInfo(null);
  });

  useEffect(() => {
    scanRef.current?.focus();
  }, []);

  function clearScan() {
    setScanValue("");
    setScanError(null);
    setScanInfo(null);
    lookup.reset();
    certLookup.reset();
    setTimeout(() => scanRef.current?.focus(), 0);
  }

  async function handleScan(raw: string) {
    setScanError(null);
    setScanInfo(null);
    const { kind, normalized } = classifyScan(raw);
    if (normalized === "") {
      setScanError("Naskenujte nebo zadejte kód.");
      return;
    }
    // Server is the only authoritative source (per AGENTS.md scan rule)
    // — we always send to lookup regardless of the local hint.
    void kind;
    let res: LookupResponse;
    try {
      res = await lookup.mutateAsync(normalized);
    } catch (err) {
      setScanError(lookupErrorMessage(err));
      return;
    }
    switch (res.kind) {
      case "voucher":
        if (res.voucher) {
          navigate(`/vouchers/${res.voucher.id}`);
        }
        return;
      case "bank_available":
        if (res.bank) {
          navigate(`/vouchers/sale?barcode=${encodeURIComponent(res.bank.barcode)}`);
        }
        return;
      case "claim":
        // Route to the unified certificates page with the scanned code pre-filled.
        navigate(`/certificates?code=${encodeURIComponent(normalized)}`);
        return;
      case "unknown":
      default:
        // Fallback: try certificate lookup for text redemption codes (CRT-…)
        // and e-shop order numbers not recognised by the voucher resolver.
        try {
          await certLookup.mutateAsync({ code: normalized });
          navigate(`/certificates?code=${encodeURIComponent(normalized)}`);
        } catch {
          setScanError("Kód nebyl v systému nalezen.");
        }
        return;
    }
  }

  async function handlePrintDailyReport() {
    setPrinting(true);
    setScanError(null);
    try {
      await printDailyReport(toApiDate(businessToday())!);
    } catch (err) {
      setScanError(err instanceof Error ? err.message : "Tisk denního reportu selhal.");
    } finally {
      setPrinting(false);
    }
  }

  const latestRides = aggregateLatestRides(summary.data?.latest_rides ?? []).slice(0, 5);

  const rideColumns: Column<AggregatedRide>[] = [
    {
      key: "time",
      header: "Čas",
      render: (r) => <Text variant="mono">{formatDateTime(r.time)}</Text>,
      tdClass: styles.colTime,
    },
    {
      key: "customer",
      header: "Zákazník",
      render: (r) => (
        <>
          <Link to={`/vouchers/${r.voucher_id}`}>{r.customer_name || "—"}</Link>
          {r.cancelled && <> <Badge variant="err">stornováno</Badge></>}
        </>
      ),
    },
    {
      key: "type",
      header: "Typ",
      render: (r) => r.voucher_type?.name ?? "—",
    },
    {
      key: "count",
      header: "Jízd",
      render: (r) => <Text variant="mono">×{r.count}</Text>,
      tdClass: styles.colCount,
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <Table.Actions>
          <Table.Action to={`/vouchers/${r.voucher_id}`}>Otevřít</Table.Action>
        </Table.Actions>
      ),
    },
  ];

  useSetTopbarCrumbs([{ label: "Skenování" }]);

  return (
    <>
      <Page.Content>
        <Page.Header
          title="Skenování"
          subtitle="výchozí obrazovka recepce"
          subtitleVariant="tagline"
        />

        <KPIGrid>
          <KPI
            label="Dnes jízd"
            value={summary.data ? summary.data.rides_today : "—"}
          />
          <KPI
            label="Odbavení zákazníci"
            value={summary.data ? summary.data.customers_today : "—"}
            delta="unikátních voucherů"
          />
          <KPI
            label="Voucherů prodáno dnes"
            value={summary.data ? summary.data.vouchers_sold_today : "—"}
            delta="recepce + e-shop"
          />
          <KPI
            label="Certifikátů objednáno dnes"
            value={summary.data ? summary.data.certificates_ordered_today : "—"}
            delta="e-shop objednávky"
          />
        </KPIGrid>

        <Card className={styles.scanHero}>
          <div className={styles.scanRow}>
            <ScanInput
              ref={scanRef}
              value={scanValue}
              onChange={(v) => {
                setScanValue(v);
                if (scanError) setScanError(null);
                if (scanInfo) setScanInfo(null);
              }}
              onScan={handleScan}
              placeholder="Skenujte nebo zadejte kód…"
              disabled={lookup.isPending || certLookup.isPending}
              variant="compact"
              className={styles.scanField}
            />
            <div className={styles.scanBtns}>
              <Button
                type="button"
                onClick={clearScan}
                disabled={!scanValue && !scanError && !scanInfo}
              >
                Smazat
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => handleScan(scanValue)}
                loading={lookup.isPending || certLookup.isPending}
              >
                Ověřit
              </Button>
            </div>
          </div>
          <div className={styles.scanHint}>
            Voucher → otevře detail · Banka kódů → prodej voucheru · Certifikát
            → směna položky
          </div>
          {scanError && (
            <div className={styles.alertSpacer}>
              <Alert variant="err">{scanError}</Alert>
            </div>
          )}
          {scanInfo && (
            <div className={styles.alertSpacer}>
              <Alert variant="info">{scanInfo}</Alert>
            </div>
          )}
          {(lookup.isPending || certLookup.isPending) && (
            <InlineLoading mono className={styles.blockLoading}>Ověřujeme kód…</InlineLoading>
          )}
        </Card>

        <ResponsiveGrid className={styles.resultGrid}>
          <Card
            bodyPadding="none"
            header={
              <Card.Header title="Posledních 5 jízd" />
            }
            footer={
              summary.data && latestRides.length > 0
                ? <Card.Meta>Poslední aktualizace: {formatDateTime(new Date())}</Card.Meta>
                : undefined
            }
          >
            <Table
              columns={rideColumns}
              rows={summary.data ? latestRides : []}
              rowKey={(r) => r.id}
              empty={
                summary.isLoading
                  ? <InlineLoading mono>Načítáme…</InlineLoading>
                  : summary.isError
                  ? <Alert variant="err">Nepodařilo se načíst poslední jízdy.</Alert>
                  : <EmptyState title="Dnes zatím nebyla zaregistrována žádná jízda." />
              }
            />
          </Card>

          <Card
            header={
              <Card.Header title="Rychlé akce" />
            }
          >
            <div className={styles.actionStack}>
              <Button block onClick={() => navigate("/vouchers")}>
                <Search size={14} /> Vyhledat voucher (jméno, telefon, kód)
              </Button>
              <Button block onClick={() => navigate("/certificates")}>
                <ArrowLeftRight size={14} /> Otevřít směnu certifikátu
              </Button>
              <Button
                block
                variant="primary"
                onClick={() => navigate("/vouchers/sale")}
              >
                <CirclePlus size={14} /> Prodej voucheru na recepci
              </Button>
              <Button
                block
                onClick={handlePrintDailyReport}
                loading={printing}
              >
                <Printer size={14} /> Tisknout denní report
              </Button>
            </div>
            <p className={styles.tip}>
              Tip: skener můžete použít kdykoli — stačí, aby byl kurzor na této
              stránce.
            </p>
          </Card>
        </ResponsiveGrid>
      </Page.Content>
    </>
  );
}
